/**
 * ReclaimRentPanel
 *
 * Lets the user pull the excess SOL rent deposit out of every token account they
 * own without closing the accounts (Solana's SIMD-0437 lowers the rent-exempt
 * minimum in steps; old accounts keep their old deposit until someone withdraws
 * it). Separate from burning on purpose: nothing is destroyed here.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Coins, X, ExternalLink, Info, RefreshCw, Share2 } from 'lucide-react';

import { Tooltip } from '@/components/Tooltip';
import { useGlobalConfig } from '@/hooks/useGlobalConfig';
import {
  useCreditsAccountStatus,
  useExcessRentScan,
  useReclaimExcessRent,
  type ReclaimExcessRentProgress,
  type ReclaimExcessRentResult,
} from '@/hooks/useReclaimExcessRent';
import { countWithdrawBatches } from '@/lib/solana/excessRent';
import { formatLamportsToSol } from '@/utils/solana/lamportsToSol';
import { getTransactionExplorerUrl, truncateSignature, type SolanaCluster } from '@/utils/solana/explorerUrl';
import config from '@/config';
import { shareToTwitter } from '@/utils/twitter';

interface ReclaimRentPanelProps {
  /** Called after a successful reclaim so the parent can refresh balances/estimates */
  onReclaimed?: () => void;
  className?: string;
}

const GOLD = '#FEBB01';
const CYAN = '#50E5F8';

/** Remembered per browser: whether reclaimed SOL should go into Junk.fun credits */
const DEPOSIT_PREFERENCE_KEY = 'junkfun.reclaimRent.depositToCredits';

function readDepositPreference(): boolean {
  try {
    return localStorage.getItem(DEPOSIT_PREFERENCE_KEY) === '1';
  } catch {
    return false;
  }
}

function storeDepositPreference(value: boolean): void {
  try {
    localStorage.setItem(DEPOSIT_PREFERENCE_KEY, value ? '1' : '0');
  } catch {
    // Storage unavailable: the toggle still works for this page load
  }
}

function getCluster(): SolanaCluster {
  return (config.SOLANA_RPC_URL || '').includes('devnet') ? 'devnet' : 'mainnet-beta';
}

function progressLabel(progress: ReclaimExcessRentProgress | null): string {
  if (!progress) return 'RECLAIMING...';
  const position = progress.totalCount > 1 ? ` (${Math.min(progress.currentIndex + 1, progress.totalCount)}/${progress.totalCount})` : '';
  switch (progress.step) {
    case 'signing':
      return `APPROVE IN WALLET${position}`;
    case 'sending':
      return `SENDING${position}`;
    case 'confirming':
      return `CONFIRMING${position}`;
    default:
      return 'DONE';
  }
}

export function ReclaimRentPanel({ onReclaimed, className = '' }: ReclaimRentPanelProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [progress, setProgress] = useState<ReclaimExcessRentProgress | null>(null);
  const [lastResult, setLastResult] = useState<ReclaimExcessRentResult | null>(null);
  const [depositToCredits, setDepositToCredits] = useState<boolean>(readDepositPreference);

  const { data: scan, isLoading, isFetching, refetch } = useExcessRentScan();
  const { data: creditsAccount } = useCreditsAccountStatus();
  const { globalConfig } = useGlobalConfig();
  const { mutateAsync: reclaim, isPending } = useReclaimExcessRent({
    onProgress: setProgress,
    onReclaimed: (result) => {
      setLastResult(result);
      onReclaimed?.();
    },
  });

  if (isLoading) return null;
  if (!scan) return null;

  const hasExcess = scan.accounts.length > 0;
  const totalSol = formatLamportsToSol(BigInt(scan.totalExcessLamports), 6);
  const txCount = countWithdrawBatches(scan.accounts);
  const skippedNote = [
    scan.skippedFrozen > 0 ? `${scan.skippedFrozen} frozen` : null,
    scan.skippedNative > 0 ? `${scan.skippedNative} wrapped SOL` : null,
  ]
    .filter(Boolean)
    .join(', ');

  // Depositing goes through the program's deposit_or_withdraw, which refuses while the
  // program is paused. A first deposit also creates the credits PDA at the wallet's
  // expense, so it is disabled when that setup cost would eat the whole reclaim.
  const depositsPaused = globalConfig?.paused === true;
  const creditsSetupLamports = creditsAccount && !creditsAccount.exists ? creditsAccount.rentLamports : 0;
  const setupCostExceedsExcess = creditsSetupLamports > 0 && scan.totalExcessLamports <= creditsSetupLamports;
  const canDepositToCredits = !depositsPaused && !setupCostExceedsExcess;
  const willDeposit = depositToCredits && canDepositToCredits;
  const setupSol = formatLamportsToSol(BigInt(creditsSetupLamports), 6);

  let depositNote: { color: string; text: string } | null = null;
  if (depositsPaused) {
    depositNote = {
      color: '#FBBF24',
      text: 'Junk.Fun deposits are paused right now, so this reclaim goes to your wallet.',
    };
  } else if (setupCostExceedsExcess) {
    depositNote = {
      color: 'rgba(255,255,255,0.55)',
      text: `Your Junk.Fun account isn't set up yet. Creating it costs ${setupSol} SOL, more than this reclaim, so the SOL goes to your wallet. Burn or deposit once to set it up.`,
    };
  } else if (willDeposit && creditsSetupLamports > 0) {
    depositNote = {
      color: 'rgba(255,255,255,0.55)',
      text: `Your Junk.Fun account isn't set up yet. Creating it costs a one-time ${setupSol} SOL from your wallet.`,
    };
  }

  const toggleDepositToCredits = (next: boolean) => {
    setDepositToCredits(next);
    storeDepositPreference(next);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setProgress(null);
    try {
      await reclaim({ scan, depositToCredits: willDeposit });
    } catch {
      // toast handled by the hook
    } finally {
      setProgress(null);
    }
  };

  // Rendered from every branch below: the success modal must survive the refetched scan
  // coming back empty after a reclaim that drained every eligible account
  const modals = (
    <>
      {showConfirm &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowConfirm(false)} />
            <div
              className="relative w-full max-w-md rounded-xl shadow-2xl"
              style={{ backgroundColor: '#343434', border: `2px solid ${GOLD}`, boxShadow: `0 0 30px ${GOLD}40` }}
            >
              <div className="flex items-center justify-between p-5" style={{ borderBottom: `2px solid ${GOLD}` }}>
                <h3
                  className="text-lg font-semibold text-white"
                  style={{ fontFamily: 'Hyperspace Race Capsule, cursive' }}
                >
                  RECLAIM EXCESS RENT
                </h3>
                <button type="button" onClick={() => setShowConfirm(false)} className="text-gray-400 hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4 p-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <div className="grid grid-cols-2 gap-3 text-center text-sm">
                  <div className="rounded p-3" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                    <div className="text-xs text-gray-400">Token accounts</div>
                    <div className="text-lg font-bold text-white">{scan.accounts.length}</div>
                  </div>
                  <div className="rounded p-3" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                    <div className="text-xs text-gray-400">{willDeposit ? 'Added to your credits' : 'You receive'}</div>
                    <div className="text-lg font-bold" style={{ color: GOLD }}>
                      {totalSol} SOL
                    </div>
                  </div>
                </div>

                <div
                  className="rounded-lg border p-3"
                  style={{
                    backgroundColor: willDeposit ? 'rgba(80, 228, 249, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    borderColor: willDeposit ? CYAN : 'rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium" style={{ color: willDeposit ? CYAN : '#FFFFFF' }}>
                        Add to my Junk.Fun credits
                      </div>
                      <div className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        {willDeposit
                          ? 'Deposited straight into your Junk.Fun account, ready for opening chests.'
                          : 'Off: the SOL stays in your wallet.'}
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={willDeposit}
                      aria-label="Add reclaimed SOL to Junk.Fun credits"
                      disabled={!canDepositToCredits}
                      onClick={() => toggleDepositToCredits(!depositToCredits)}
                      className="relative h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      style={{ backgroundColor: willDeposit ? CYAN : 'rgba(255, 255, 255, 0.2)' }}
                    >
                      <span
                        className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform"
                        style={{ transform: willDeposit ? 'translateX(20px)' : 'translateX(0)' }}
                      />
                    </button>
                  </div>
                  {depositNote && (
                    <div className="mt-2 text-xs" style={{ color: depositNote.color }}>
                      {depositNote.text}
                    </div>
                  )}
                </div>

                <ul className="space-y-1.5 text-sm" style={{ color: 'rgba(255,255,255,0.85)' }}>
                  <li>• Your tokens and NFTs are untouched. Nothing is burned or closed.</li>
                  <li>
                    • Only the deposit above Solana's current minimum moves
                    {willDeposit ? ', straight into your Junk.Fun credits.' : ' to your wallet.'}
                  </li>
                  <li>• No Junk.fun fee. You pay the network fee only.</li>
                  <li>
                    • {txCount} transaction{txCount === 1 ? '' : 's'} to approve.
                  </li>
                </ul>

                <div className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Solana's current minimum for a token account is {formatLamportsToSol(BigInt(scan.tokenAccountMinimum), 6)} SOL.
                  Further reductions are planned, so more may become reclaimable later.
                </div>

                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowConfirm(false)}
                    className="flex-1 rounded-lg border px-4 py-2 text-sm text-white hover:bg-white/10"
                    style={{ borderColor: 'rgba(255,255,255,0.3)' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="flex-1 rounded-lg px-4 py-2 text-sm font-bold text-black hover:scale-105"
                    style={{
                      background:
                        'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                    }}
                  >
                    {willDeposit ? `Reclaim ${totalSol} SOL to credits` : `Reclaim ${totalSol} SOL`}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {lastResult &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setLastResult(null)} />
            <div
              className="relative w-full max-w-md rounded-xl shadow-2xl"
              style={{ backgroundColor: '#343434', border: `2px solid ${CYAN}`, boxShadow: `0 0 30px ${CYAN}40` }}
            >
              <div className="flex items-center justify-between p-5" style={{ borderBottom: `2px solid ${CYAN}` }}>
                <h3
                  className="text-lg font-semibold text-white"
                  style={{ fontFamily: 'Hyperspace Race Capsule, cursive' }}
                >
                  RENT RECLAIMED
                </h3>
                <button type="button" onClick={() => setLastResult(null)} className="text-gray-400 hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4 p-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <div className="text-center">
                  <div className="text-3xl font-bold" style={{ color: CYAN }}>
                    +{formatLamportsToSol(BigInt(lastResult.reclaimedLamports), 6)} SOL
                  </div>
                  <div className="mt-1 text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {lastResult.depositedToCredits ? 'added to your Junk.Fun credits from' : 'sent to your wallet from'}{' '}
                    {lastResult.accountsReclaimed} token account
                    {lastResult.accountsReclaimed === 1 ? '' : 's'}. Your tokens are still there.
                  </div>
                </div>
                <div className="space-y-1 text-xs">
                  {lastResult.signatures.map((signature) => (
                    <a
                      key={signature}
                      href={getTransactionExplorerUrl(signature, getCluster())}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between rounded px-3 py-2 hover:bg-white/10"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: CYAN }}
                    >
                      <span className="font-mono">{truncateSignature(signature, 8, 8)}</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    shareToTwitter(
                      `Just claimed ${formatLamportsToSol(BigInt(lastResult.reclaimedLamports), 4)} SOL from my @junkfun_ account thanks to Solana’s rent reduction ♻️\n\nCheck yours: junk.fun`
                    )
                  }
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-600"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  <Share2 className="h-4 w-4" />
                  Share on X
                </button>
                <button
                  type="button"
                  onClick={() => setLastResult(null)}
                  className="w-full rounded-lg px-4 py-2 text-sm font-bold text-black"
                  style={{ backgroundColor: CYAN }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );

  // Nothing to reclaim right now: keep it to one quiet line so the page stays uncluttered
  if (!hasExcess) {
    if (scan.scannedAccounts === 0) return modals;
    return (
      <>
        <div
          className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${className}`}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.15)',
            color: 'rgba(255, 255, 255, 0.6)',
            fontFamily: 'Outfit, sans-serif',
          }}
        >
          <span className="flex items-center gap-2">
            <Coins className="h-3.5 w-3.5" style={{ color: 'rgba(255,255,255,0.5)' }} />
            Your token accounts hold no excess rent right now. Check back after Solana's next rent reduction step.
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex-shrink-0 rounded p-1 hover:bg-white/10 disabled:opacity-50"
            aria-label="Re-check excess rent"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {modals}
      </>
    );
  }

  return (
    <>
      <div
        className={`flex flex-col gap-3 rounded-lg border-2 p-4 sm:flex-row sm:items-center sm:justify-between ${className}`}
        style={{
          // Solid card: the page sits on busy artwork, so a tinted overlay disappears.
          backgroundColor: '#1B1B1B',
          borderColor: GOLD,
          boxShadow: `0 0 24px ${GOLD}55`,
        }}
      >
        <div className="flex min-w-0 items-start gap-3">
          <div
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: GOLD }}
          >
            <Coins className="h-5 w-5 text-black" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span
                className="text-base font-bold uppercase tracking-wide"
                style={{ fontFamily: 'Tiny5, monospace', color: GOLD }}
              >
                Excess rent available
              </span>
              <Tooltip
                position="bottom"
                content={
                  <div className="max-w-xs text-xs leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Solana lowered the SOL deposit every account needs (SIMD-0437). Accounts you already
                    have still hold the old, larger deposit. This withdraws only the difference back to
                    your wallet, or into your Junk.Fun credits if you turn that on in the confirm step.
                    Your tokens and accounts stay exactly as they are, nothing is burned or closed, and
                    there is no Junk.fun fee. Each future rent reduction step frees up more.
                  </div>
                }
              >
                <Info className="h-3.5 w-3.5 cursor-help" style={{ color: 'rgba(255,255,255,0.6)' }} />
              </Tooltip>
            </div>
            <div
              className="text-sm"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)' }}
            >
              {scan.accounts.length} token account{scan.accounts.length === 1 ? '' : 's'} hold more SOL than
              Solana now requires. Withdraw the difference without closing anything.
              {skippedNote && (
                <span style={{ color: 'rgba(255, 255, 255, 0.55)' }}> Skipped: {skippedNote}.</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-shrink-0">
          <div
            className="rounded-lg border px-3 py-1.5 text-center text-base font-bold"
            style={{
              fontFamily: 'Outfit, sans-serif',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              borderColor: `${GOLD}80`,
              color: GOLD,
            }}
          >
            +{totalSol} SOL
          </div>
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            disabled={isPending || isFetching}
            className="w-full rounded-lg px-4 py-2 text-xs font-bold text-black transition-all hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:text-sm"
            style={{
              fontFamily: 'Outfit, sans-serif',
              background: isPending
                ? 'rgba(254, 187, 1, 0.6)'
                : 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
            }}
          >
            {isPending ? progressLabel(progress) : 'RECLAIM EXCESS RENT'}
          </button>
        </div>
      </div>

      {modals}
    </>
  );
}
